import { memo, type ReactNode } from 'react';
import { useFocusRing, useHover } from 'react-aria';
import cn from 'classnames';
import { type TestProps } from '@direct-frontend/components/testing';
import { useRadioState } from '../../../../lib/hooks';
import { makeModifierClassname } from '../../../../lib/makeModifierClassname';
import { RadioGroupContext } from '../../lib/RadioGroupContext';
import styles from './RadioItem.module.css';

export interface RadioItemProps extends TestProps {
    children?: ReactNode;
    value: string;
    isDisabled?: boolean;
    isDashed?: boolean;
    className?: string;
    'aria-label'?: string;
    title?: string;
    isCircle?: boolean;
}

const radioItemModifier = makeModifierClassname(styles, 'radioItem');

export const RadioItem = memo(function RadioItem(props: RadioItemProps) {
    const {
        'data-testid': dataTestId = 'RadioItem',
        children,
        value,
        isDisabled = false,
        isDashed = false,
        isCircle = false,
        className,
        'aria-label': ariaLabel,
        title,
    } = props;

    const { selectedValue, setSelectedValue, name } = useRadioState(RadioGroupContext);

    const isSelected = selectedValue === value;
    const isItemDisabled = isDisabled && !isSelected;

    const handleClick = () => {
        if (!isItemDisabled && setSelectedValue) {
            setSelectedValue(value);
        }
    };

    const { hoverProps, isHovered } = useHover({ ...props, isDisabled });
    const { focusProps, isFocused } = useFocusRing({ ...props, isTextInput: true });

    return (
        <label
            {...hoverProps}
            {...focusProps}
            className={cn(
                className,
                radioItemModifier.className,
                radioItemModifier('selected', isSelected),
                radioItemModifier('disabled', isItemDisabled),
                radioItemModifier('dashed', isDashed && !isItemDisabled && !isSelected),
                radioItemModifier('circle', isCircle),
                {
                    [styles.hovered]: isHovered,
                    [styles.focused]: isFocused,
                    [styles.disabled]: isDisabled,
                }
            )}
            title={title || value}
            data-testid={dataTestId}
        >
            <input
                type="radio"
                name={name}
                value={value}
                checked={isSelected}
                disabled={isItemDisabled}
                onChange={handleClick}
                className={styles.hiddenInput}
                aria-label={ariaLabel || `Выбрать ${value}`}
                data-testid={`${dataTestId}.Input`}
            />

            <div data-testid={`${dataTestId}.Content`} className={cn(styles.content, 'border-radius-6')}>
                {children}
            </div>
        </label>
    );
});
